import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { initAttendanceApi } from "../../services/attendanceApi";
import { ToastProvider } from "./components/Toast";

// Employee components
import CheckInCard from "./components/CheckInCard";
import AttendanceCalendar from "./components/AttendanceCalendar";
import MonthlyStats from "./components/MonthlyStats";
import LeaveApplyForm from "./components/LeaveApplyForm";
import LeaveBalance from "./components/LeaveBalance";
import LeaveHistory from "./components/LeaveHistory";
import PermissionForm from "./components/PermissionForm";
import CorrectionForm from "./components/CorrectionForm";

// Admin components
import AdminOverview from "./components/AdminOverview";
import AdminApprovals from "./components/AdminApprovals";
import AdminSettings from "./components/AdminSettings";
import ReportExport from "./components/ReportExport";

import "./styles/attendance-base.css";
import "./styles/attendance-calendar.css";
import "./styles/attendance-leave.css";
import "./styles/attendance-admin.css";
import "./styles/attendance-responsive.css";

/**
 * AttendanceLeaveApp — main orchestrator for the Attendance & Leave module.
 *
 * Three tabs:
 *   - Attendance : check in/out, monthly calendar, stats  (all users)
 *   - Leaves     : apply leave/permission, balance, history (all users)
 *   - Admin      : overview, approvals, settings, reports  (ADMIN only)
 *
 * Renders inside PlatformShell (receives user + userRole as props),
 * so it inherits the existing MSAL session — no separate login.
 */

const Icon = {
  clock: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
  ),
  leave: (
    <svg viewBox="0 0 24 24"><path d="M3 7h18M3 12h18M3 17h12" /></svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24"><path d="M12 2l7 4v6c0 4-3 7-7 8-4-1-7-4-7-8V6z" /></svg>
  ),
};

function AttendanceLeaveApp({ user, userRole }) {
  const { instance } = useMsal();
  const isAdmin = userRole === "ADMIN";
  const [activeTab, setActiveTab] = useState("attendance");

  // Refresh signal — bumped after check-in/out or leave apply so child
  // views re-fetch (calendar, stats, balance stay in sync).
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });

  // When the employee clicks "Request a correction" on the CheckInCard, jump
  // to the Leaves tab (where CorrectionForm lives) and scroll it into view.
  // The tab switch re-renders, so we scroll on the next frame once the form
  // is mounted. The target element is tagged with id="atl-correction-form".
  const goToCorrection = () => {
    setActiveTab("leaves");
    setTimeout(() => {
      try {
        const el = document.getElementById("atl-correction-form");
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        /* no-op — scrolling is a best-effort nicety */
      }
    }, 120);
  };

  // Live IST clock (updates every second) — shown in the app header
  const [clock, setClock] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const clockTime = clock.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const clockDate = clock.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", day: "numeric", month: "short", year: "numeric" });

  // Dark mode — persisted in localStorage
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("atl_theme") === "dark"; } catch { return false; }
  });
  const toggleDark = () => {
    setDark((v) => {
      const next = !v;
      try { localStorage.setItem("atl_theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  };

  // Initialize the API with the shared MSAL instance SYNCHRONOUSLY on every
  // render, so child components never call the API before the instance is set
  // (prevents a redirect/refresh loop on first mount).
  if (instance) initAttendanceApi(instance);

  const tabs = [
    { id: "attendance", name: "Attendance", icon: Icon.clock },
    { id: "leaves", name: "Leaves", icon: Icon.leave },
    ...(isAdmin ? [{ id: "admin", name: "Admin", icon: Icon.admin }] : []),
  ];

  return (
    <ToastProvider>
    <div className={`atl-app ${dark ? "atl-app--dark" : ""}`}>
      {/* ─── App header with live IST clock ─── */}
      <div className="atl-appbar">
        <div className="atl-appbar__title">
          <span className="atl-appbar__ic">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
          </span>
          <div>
            <h2>Attendance &amp; Leave</h2>
            <span className="atl-appbar__sub">Cloud TechOn</span>
          </div>
        </div>
        <div className="atl-appbar__right">
          <button className="atl-theme-btn" onClick={toggleDark} aria-label="Toggle theme" title="Toggle dark mode">
            {dark ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
            )}
          </button>
          <div className="atl-clock">
            <span className="atl-clock__time">{clockTime}</span>
            <span className="atl-clock__date">{clockDate} · IST</span>
          </div>
        </div>
      </div>

      {/* ─── Tab bar ─── */}
      <div className="atl-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`atl-tab ${activeTab === t.id ? "active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            <span className="atl-tab__ic">{t.icon}</span>
            <span>{t.name}</span>
          </button>
        ))}
      </div>

      {/* ─── Tab content ─── */}
      <div className="atl-content">
        {activeTab === "attendance" && (
          <div className="atl-grid">
            <CheckInCard onChange={refresh} onRequestCorrection={goToCorrection} />
            <MonthlyStats refreshKey={refreshKey} month={period.month} year={period.year} />
            <div className="atl-span-2"><AttendanceCalendar refreshKey={refreshKey} onPeriodChange={setPeriod} /></div>
          </div>
        )}

        {activeTab === "leaves" && (
          <div className="atl-grid">
            <div className="atl-col">
              <LeaveBalance refreshKey={refreshKey} />
              <PermissionForm refreshKey={refreshKey} />
            </div>
            <div className="atl-col">
              <LeaveApplyForm onApplied={refresh} refreshKey={refreshKey} />
              <CorrectionForm onSubmitted={refresh} refreshKey={refreshKey} />
            </div>
            <div className="atl-span-2"><LeaveHistory refreshKey={refreshKey} onCancelled={refresh} /></div>
          </div>
        )}

        {activeTab === "admin" && isAdmin && (
          <AdminTabs />
        )}
      </div>
    </div>
    </ToastProvider>
  );
}

/* ─── Admin sub-tabs (inside the Admin tab) ─── */
function AdminTabs() {
  const [sub, setSub] = useState("overview");
  const subTabs = [
    { id: "overview", name: "Today Overview" },
    { id: "approvals", name: "Approvals" },
    // { id: "shifts", name: "Shifts" },   // Enable when backend shift routes are deployed
    { id: "settings", name: "Settings" },
    { id: "reports", name: "Reports" },
  ];
  return (
    <div className="atl-admin">
      <div className="atl-subtabs">
        {subTabs.map((s) => (
          <button
            key={s.id}
            className={`atl-subtab ${sub === s.id ? "active" : ""}`}
            onClick={() => setSub(s.id)}
          >
            {s.name}
          </button>
        ))}
      </div>
      <div className="atl-admin__body">
        {sub === "overview" && <AdminOverview />}
        {sub === "approvals" && <AdminApprovals />}
        {sub === "settings" && <AdminSettings />}
        {sub === "reports" && <ReportExport />}
      </div>
    </div>
  );
}

export default AttendanceLeaveApp;
